import { ImpactShopProductType } from "@ecologi/shared";
import {
  GetServerSidePropsContext,
  NextApiRequest,
  NextApiResponse,
} from "next";
import { ImpactShopProductPage } from "../../../features";
import { auth0, getImpactShopProduct } from "../../../lib";
import { withLogging } from "../../../lib/ssr/logGetServerSideProps";
import { createServerTRPCClient } from "../../../lib/trpc/server";
import { prefetchImpactGroupsForSSR } from "../../../features/impact-shop/hooks/useListImpactGroups";
import { QueryCache } from "react-query";
import { dehydrate } from "react-query/hydration";
import { getAuth0AccessToken } from "../../../lib/auth0";
import logger from "../../../lib/logger";

type ImpactShopProductPageComponentProps = {
  isLoggedIn: boolean;
  product: ImpactShopProductType;
  impactGroupsEnabled?: boolean;
};

const ImpactShopProductPageComponent = ({
  product,
  isLoggedIn,
  impactGroupsEnabled,
}: ImpactShopProductPageComponentProps) => (
  <ImpactShopProductPage
    isLoggedIn={isLoggedIn}
    product={product}
    impactGroupsEnabled={impactGroupsEnabled}
  />
);

const redirectToImpactShop = {
  redirect: {
    destination: "/impact-shop",
    permanent: false,
  },
} as const;

export const getServerSideProps = withLogging(
  async ({ params, req, res }: GetServerSidePropsContext) => {
    const tokenCache = auth0.tokenCache(
      req as NextApiRequest,
      res as NextApiResponse
    );

    const session = await auth0.getSession(req);

    try {
      const product = await getImpactShopProduct(
        params.slug as string,
        session ? tokenCache : undefined
      );

      if (!product) {
        return redirectToImpactShop;
      }

      let dehydratedState: ReturnType<typeof dehydrate> | undefined;

      if (session) {
        const accessToken = await getAuth0AccessToken(req, res);

        if (accessToken) {
          try {
            const queryCache = new QueryCache();

            const serverTrpc = createServerTRPCClient(accessToken);
            await prefetchImpactGroupsForSSR(queryCache, serverTrpc);

            dehydratedState = dehydrate(queryCache);
          } catch (error) {
            logger.error("Failed to prefetch impact groups:", { error });
          }
        }
      }

      return {
        props: {
          layoutProps: {
            hideHeader: true,
            isWhiteBody: true,
            isProfile: true,
          },
          isLoggedIn: Boolean(session?.user),
          product,
          ...(dehydratedState && { dehydratedState }),
        },
      };
    } catch {
      return redirectToImpactShop;
    }
  }
);

export default ImpactShopProductPageComponent;
